namespace ui
{
	export namespace TwitterBootstrap3_3
	{
		export interface IGridSize
		{
			xs: number;
			sm: number;
			md: number;
			lg: number;
		}
	}

	export namespace base
	{
		export type AnyFunction = (...args: any[]) => any;

		export interface IBaseHTMLElementDescriptor
		{
			id?: string;
			attr?: {[key: string]: string};
			classes?: string[];
			on?: { [key: string]: AnyFunction };
			role?: string;
			pattern?: string;
			css?: {[key: string]: string};
			aria?:
			{
				label?: string
				labelledby?: string
				describedby?: string
				hidden?: boolean
				disabled?: boolean
				checked?: boolean
				grabbed?: boolean
				[key: string]: any|undefined
			};
		}

		export class BaseHTMLElement<T extends HTMLElement = HTMLElement>
		{
			public	UUID:		string;

			protected	m_Root: JQuery;

			public constructor( tagName: string )
			{
				this.UUID		= ui.utils.UUID.Gen() + "-" + ui.utils.UUID.Gen();
				this.m_Root	= $( document.createElement( tagName ) );
			}

			public Initialize( descriptor: IBaseHTMLElementDescriptor )
			{
				// Adding id if present
				if ( descriptor.id )
				{
					this.attr( 'id', descriptor.id );
				}

				// Adding attributes if present
				if ( descriptor.attr )
				{
					for ( const attr in descriptor.attr )
					{
						if ( descriptor.attr[ attr ] === undefined )
						{
							continue;
						}

						this.attr( attr, descriptor.attr[ attr ] );
					}
				}

				// Adding classes if present
				if ( descriptor.classes )
				{
					for ( const _class in descriptor.classes )
					{
						if ( descriptor.classes[ _class ] === undefined )
						{
							continue;
						}

						this.m_Root.addClass( descriptor.classes[ _class ] );
					}
				}

				if ( descriptor.on )
				{
					Object.keys( descriptor.on ).forEach(
						( name ) =>
						{
							// @ts-ignore
							const event = descriptor.on[ name ];

							this.on( event.name, ( ...args: any[] ) => event.call( this, args) );
						}
					);
				}

				if ( descriptor.css )
				{
					this.m_Root.css( descriptor.css );
				}

				// Adding ARIA support
				if ( descriptor.aria )
				{
					for ( const tmp_aria in descriptor.aria )
					{
						if ( descriptor.aria[ tmp_aria ] === undefined )
						{
							continue;
						}

						let aria = descriptor.aria[ tmp_aria ];

						if ( typeof aria === 'boolean' )
						{
							aria = aria ? 'true' : 'false';
						}
						else if ( typeof aria === 'number' )
						{
							aria = aria.toString();
						}
						else if ( typeof aria !== 'string' )
						{
							continue;
						}

						this.m_Root.attr( 'aria-' + tmp_aria, aria );
					}
				}

				// Adding ARIA role support
				if ( descriptor.role )
				{
					this.m_Root.attr( 'role', descriptor.role );
				}

				if ( descriptor.pattern )
				{
					this.attr( 'pattern', descriptor.pattern );
				}
			}

			public empty(): BaseHTMLElement<T>
			{
				this.Root().empty();

				return this;
			}

			public remove(): BaseHTMLElement<T>
			{
				this.Root().remove();

				return this;
			}

			public text(): string;
			public text( text: string ): BaseHTMLElement<T>;

			public text( text?: string ): string|BaseHTMLElement<T>
			{
				if ( text )
				{
					this.Root().text( text );

					return this;
				}

				return this.Root().text();
			}

			public append( element: BaseHTMLElement | JQuery | string | Text ): BaseHTMLElement<T>
			{
				if ( element instanceof BaseHTMLElement )
				{
					element = element.Root();
				}
				else if ( typeof element === 'string' )
				{
					element = ui.text( element );
				}

				this.Root().append( element );

				return this;
			}

			public appendTo( root: BaseHTMLElement<HTMLElement> | JQuery ): BaseHTMLElement<T>
			{
				if ( root instanceof BaseHTMLElement )
				{
					root = root.Root();
				}

				root.append( this.Root() );

				return this;
			}

			public hasClass( className: string ): boolean
			{
				return this.Root().hasClass( className );
			}

			public addClass( className: string ): BaseHTMLElement<T>
			{
				this.Root().addClass( className );

				return this;
			}

			public remClass( className: string ): BaseHTMLElement<T>
			{
				this.Root().removeClass( className );

				return this;
			}

			/**
			 * @returns The content of this element as a string.
			 */
			public html(): string;
			/**
			 * Sets the content of this element by given JQuery or ui.base.BaseHTMLElement.
			 *
			 * If the parameter is a string, it will be parsed as a HTML string, if the string
			 * begins with a <TAG> and will be parsed until the end-</TAG> is detected.
			 *
			 * @param element Element to be set as content of this element.
			 */
			public html( element: JQuery|ui.base.BaseHTMLElement|string ): ui.base.BaseHTMLElement<T>;

			public html( element?: JQuery|ui.base.BaseHTMLElement|string ): ui.base.BaseHTMLElement<T>|string
			{
				if ( element )
				{
					if ( ! ( typeof element === 'string' ) )
					{
						element = element.html();
					}

					this.Root().html( element );

					return this;
				}

				return this.Root().html();
			}

			public on( name: string, func: AnyFunction ): BaseHTMLElement<T>;
			public on( obj: {[key: string]: AnyFunction } ): BaseHTMLElement<T>;

			public on( obj: string|{[key: string]: AnyFunction }, func?: AnyFunction ): BaseHTMLElement<T>
			{
				if ( typeof obj === 'string' )
				{
					// tslint:disable-next-line:only-arrow-functions
					this.m_Root.on( obj, function() { if ( func ) { func.call( null, arguments ); } } );
				}
				else
				{
					this.m_Root.on( obj );
				}

				return this;
			}

			public attr( attribute: string ):  string|boolean;
			public attr( attribute: { [ key: string ]: string|number|boolean} ): BaseHTMLElement<T>;
			public attr( attribute: string, value: string|number|boolean ): BaseHTMLElement<T>;

			public attr(
				attribute: string|{[key: string]: string|number|boolean},
				value?: string|number|boolean
			): BaseHTMLElement<T>|string|boolean
			{
				if ( typeof attribute !== 'string' )
				{
					this.m_Root.attr( attribute );

					return this;
				}

				if ( value === undefined )
				{
					if ( $.inArray( attribute, [ 'checked', 'selected', 'disabled', 'readonly' ] ) > -1 )
					{
						return this.m_Root.prop( attribute );
					}

					return this.m_Root.attr( attribute ) || 'undefined';
				}

				if ( $.inArray( attribute, [ 'checked', 'selected', 'disabled', 'readonly' ] ) > -1 )
				{
					this.m_Root.prop( attribute, value === true );

					return this;
				}

				this.m_Root.attr( attribute, value.toString() );

				return this;
			}

			public hasAttr( attribute: string ): boolean
			{
				return this.m_Root.attr( attribute ) !== undefined;
			}

			public Root(): JQuery
			{
				return this.m_Root;
			}

			public css( property: string, value: string ): BaseHTMLElement<T>;
			public css( properties: {[key: string]: string} ): BaseHTMLElement<T>;

			public css( property: string|{[key: string]: string}, value?: string ): BaseHTMLElement<T>
			{
				if ( typeof property === 'string' )
				{
					const styling = property;

					property = {};
					property[ styling ] = value || '';
				}

				this.m_Root.css( property );

				return this;
			}
		}
	}
}
